import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import Brand from "@/components/Brand";

const HERO_IMAGE = "https://images.unsplash.com/photo-1748551204300-f227d5af350f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwbW9kZXJuJTIwcmVzdGF1cmFudCUyMGludGVyaW9yJTIwZGFya3xlbnwwfHx8fDE3NzgxODkyNjN8MA&ixlib=rb-4.1.0&q=85";

const Login = () => {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    if (user) return <Navigate to="/" replace />;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        const res = await login(email, password);
        setSubmitting(false);
        if (res.ok) {
            toast.success("Accesso effettuato");
            navigate("/");
        } else {
            setError(res.error);
        }
    };

    return (
        <div className="min-h-screen flex flex-col lg:flex-row bg-deep relative">
            {/* Super Admin discreet button — top right corner */}
            <button
                onClick={() => navigate("/superadmin")}
                data-testid="superadmin-button"
                title="Pannello Super Admin (riservato)"
                className="fixed top-4 right-4 z-[10000] w-11 h-11 rounded-full border border-red-700/40 bg-deep/80 backdrop-blur-sm text-red-400/70 hover:text-red-300 hover:border-red-500 hover:bg-red-950/40 transition-all flex items-center justify-center font-bold text-xs tracking-wider shadow-lg"
            >
                SA
            </button>
            <div className="hidden lg:flex lg:w-[55%] relative bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMAGE})` }}>
                <div className="absolute inset-0 bg-deep/75" />
                <div className="absolute inset-0 bg-gradient-to-br from-brand/10 to-transparent" />
                <div className="relative z-10 flex flex-col justify-between p-16 w-full">
                    <Brand size="lg" />
                    <div className="max-w-xl">
                        <div className="text-xs uppercase tracking-[0.3em] text-brand mb-4">Gestionale Ristorante & Pizzeria</div>
                        <h1 className="font-display text-5xl xl:text-6xl font-bold text-warm leading-[1.05] mb-6">
                            Il servizio<br /><span className="text-brand">a 360°.</span>
                        </h1>
                        <p className="text-elegant text-lg leading-relaxed">
                            Tavoli, comande, cucina e cassa connessi in tempo reale.
                            Ogni ristorante con il suo spazio dedicato, completamente isolato.
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-surface">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-10"><Brand size="md" /></div>

                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-3">Accesso</div>
                    <h2 className="font-display text-4xl font-bold text-warm mb-2">Bentornato.</h2>
                    <p className="text-elegant mb-8">
                        Accedi come Admin, Cameriere, Cassa o Cucina,<br />
                        <span className="text-brand">il sistema ti riconosce automaticamente.</span>
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                        <div>
                            <label className="block text-xs uppercase tracking-wider text-elegant mb-2">Email</label>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input"
                                className="w-full h-12 px-4 bg-surface-elevated border border-[#1f2c44] rounded-md text-warm placeholder-[#5a6a85] focus:border-brand focus:outline-none transition-colors"
                                placeholder="il-tuo-email@locale.it" />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-wider text-elegant mb-2">Password</label>
                            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input"
                                className="w-full h-12 px-4 bg-surface-elevated border border-[#1f2c44] rounded-md text-warm placeholder-[#5a6a85] focus:border-brand focus:outline-none transition-colors"
                                placeholder="••••••••" />
                        </div>
                        {error && <div className="text-sm text-red-400" data-testid="login-error">{error}</div>}
                        <button type="submit" disabled={submitting} data-testid="login-submit-button"
                            className="w-full h-12 bg-brand hover:bg-brand-light text-deep font-display font-bold uppercase tracking-[0.15em] rounded-md transition-colors disabled:opacity-50">
                            {submitting ? "Accesso..." : "Accedi"}
                        </button>
                    </form>

                    <div className="mt-8 pt-6 border-t border-[#1f2c44] text-center">
                        <p className="text-elegant text-sm mb-3">Non hai ancora un account?</p>
                        <Link to="/registrazione" data-testid="goto-register" className="inline-flex items-center gap-2 h-11 px-5 border border-brand text-brand hover:bg-brand/10 rounded-md text-sm uppercase tracking-wider font-bold">
                            Registra il tuo ristorante →
                        </Link>
                        <p className="text-[11px] text-elegant mt-3">3 giorni di prova gratuita · nessuna carta richiesta</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
